import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Pencil, ArrowLeft } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabaseApplicantService } from '../../../services/supabase/applicants';
import { useNavigationRefresh } from '../../../hooks/useNavigationRefresh';
import EditApplicantModal from './components/EditApplicantModal';
import AddApplicantModal from './components/AddApplicantModal';
import type { Applicant } from '../../../types/applicant';

export default function ApplicantsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Use the custom hook to handle navigation refreshes
  useNavigationRefresh(['admin', 'applicants']);

  const { data: applicants = [], isLoading } = useQuery({
    queryKey: ['admin', 'applicants'],
    queryFn: supabaseApplicantService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const filteredApplicants = applicants.filter(applicant => 
    applicant.appl_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    applicant.appl_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    applicant.appl_phone?.includes(searchQuery)
  );

  const handleEdit = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setIsEditModalOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'applicants'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Manage Applicants</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage lease applicants
          </p>
        </div>
        <button 
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Applicant
        </button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search applicants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Filter className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-60">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredApplicants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No applicants found
                  </td>
                </tr>
              ) : (
                filteredApplicants.map((applicant) => (
                  <tr key={applicant.appl_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(applicant)}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="truncate w-60" title={applicant.appl_name}>
                        {applicant.appl_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {applicant.appl_cont_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="truncate w-40" title={applicant.appl_email || 'N/A'}>
                        {applicant.appl_email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {applicant.appl_phone || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <AddApplicantModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedApplicant && isEditModalOpen && (
        <EditApplicantModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedApplicant(null);
          }}
          onSuccess={handleSuccess}
          applicant={selectedApplicant}
        />
      )}
    </div>
  );
}
